/**
 * @module components/LinkEntityModal
 * @description Modal zum Verknüpfen eines Cases mit einem Claim (oder umgekehrt).
 *   ILIKE-Suche debounced 300ms, optionaler customer_id-Vorfilter, exclude_linked_to_*-Filter.
 *
 * #1246 — Smart-Buttons + bidirektionale Claim↔Case-Verlinkung.
 *
 * @since v2.539.0
 */
import { useEffect, useState } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

export default function LinkEntityModal({
  isOpen,
  onClose,
  mode,              // 'pick-case' (von Claim aus) | 'pick-claim' (von Case aus)
  sourceId,          // claim_id wenn mode='pick-case', sonst case_id
  customerId,
  onLinked,
}) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery(''); setResults([]); setSelectedId(null); setNote(''); setError(null);
      return;
    }
    // Initial-Load: leere Suche → letzte Treffer (limit 20)
    runSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose?.(); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  async function runSearch(q) {
    setSearching(true);
    setError(null);
    try {
      const path = mode === 'pick-case' ? '/api/cases/search' : '/api/claims/search';
      const excludeKey = mode === 'pick-case' ? 'exclude_linked_to_claim' : 'exclude_linked_to_case';
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (customerId) params.set('customer_id', String(customerId));
      if (sourceId) params.set(excludeKey, String(sourceId));
      params.set('limit', '20');
      const r = await fetch(`${API}${path}?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setResults(data.rows || []);
    } catch (err) {
      setError(err.message || 'Suche fehlgeschlagen');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function submit() {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      const path = mode === 'pick-case'
        ? `/api/claims/${sourceId}/link-case`
        : `/api/cases/${sourceId}/link-claim`;
      const body = mode === 'pick-case'
        ? { case_id: selectedId, note: note.trim() || undefined }
        : { claim_id: selectedId, note: note.trim() || undefined };
      const r = await fetch(`${API}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.status === 409) {
        setError(t('links.alreadyLinked', 'Diese Verknüpfung existiert bereits.'));
        return;
      }
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const link = await r.json();
      onLinked?.(link);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Speichern fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const pickingCase = mode === 'pick-case';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-surface-card border border-line-card rounded-card w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-line-card">
          <h3 className="text-[15px] font-bold text-content">
            {pickingCase
              ? t('links.modal.titleCase', 'Case verknüpfen')
              : t('links.modal.titleClaim', 'Klärung verknüpfen')}
          </h3>
          <button onClick={onClose} className="text-content-tertiary hover:text-content">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={pickingCase
                ? t('links.modal.searchCase', 'Case-Titel / ID / Kunde suchen...')
                : t('links.modal.searchClaim', 'Klärungs-Nr / Tracking / Kunde suchen...')}
              autoFocus
              className="w-full pl-9 pr-3 py-2 text-[13px] border border-line-card rounded-lg bg-surface-card focus:outline-none focus:border-[var(--text-link,#338edc)]"
            />
          </div>

          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>
          )}

          <div className="space-y-1 min-h-[200px]">
            {searching && (
              <div className="flex items-center gap-2 text-[12px] text-content-tertiary py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('common.searching', 'Sucht...')}</span>
              </div>
            )}
            {!searching && results.length === 0 && (
              <div className="text-center py-8 text-[12px] text-content-tertiary">
                {t('links.modal.noResults', 'Keine Treffer.')}
              </div>
            )}
            {!searching && results.map((row) => {
              const isSelected = selectedId === row.id;
              const title = pickingCase ? row.title : row.claim_number;
              const subtitle = pickingCase
                ? `#${row.id} • ${row.customer_name || '—'} • ${row.stage || ''}`
                : `${row.customer_name || '—'} • ${row.claim_type || ''} • ${row.status || ''}`;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setSelectedId(isSelected ? null : row.id)}
                  className={`w-full text-left p-2.5 rounded border transition-colors ${
                    isSelected
                      ? 'border-[var(--text-link,#338edc)] bg-blue-50'
                      : 'border-line-card hover:bg-surface-hover'
                  }`}
                >
                  <div className="text-[13px] font-semibold text-content truncate">{title || '—'}</div>
                  <div className="text-[11px] text-content-tertiary truncate">{subtitle}</div>
                </button>
              );
            })}
          </div>

          {selectedId && (
            <div>
              <label className="block text-[11px] font-medium text-content-secondary mb-1">
                {t('links.modal.noteLabel', 'Notiz (optional)')}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2 text-[13px] border border-line-card rounded-lg bg-surface-card focus:outline-none focus:border-[var(--text-link,#338edc)] resize-none"
                placeholder={t('links.modal.notePlaceholder', 'z.B. Bezug, Begründung...')}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-line-card">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-[13px] font-medium text-content-secondary hover:bg-surface-hover rounded transition-colors disabled:opacity-50"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!selectedId || submitting}
            className="px-4 py-2 text-[13px] font-medium bg-[var(--text-link,#338edc)] text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t('links.modal.confirm', 'Verknüpfen')}
          </button>
        </div>
      </div>
    </div>
  );
}
